import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { Agencies } from 'app/models/agencies';
import { AgenciesService } from 'app/services/agencies/agencies.service';
import { AwscontrolService } from 'app/services/aws/awscontrol.service';
import { Location } from '@angular/common';
import Swal from 'sweetalert2';

import { AGENCES_SERVICES } from "./../classesTs/agences-services";
import { MATERIAUX } from "./../classesTs/Materiax";
import { VILLES } from "./../classesTs/Ville";

interface Role {
    value: string;
    viewValue: string;
}

@Component({
    selector: 'app-modifier-agencie',
    templateUrl: './modifier-agencie.component.html',
    styleUrls: ['./modifier-agencie.component.scss']
})
export class ModifierAgencieComponent implements OnInit {
    private version = Date.now();
    selectedFiles1!: FileList;
    isEditMode = false;
    agencieData: Agencies;
    dataSource = new MatTableDataSource();
    displayedColumns: string[] = [];
    @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;
    @ViewChild('agencyForm') public currentForm!: NgForm;
    @ViewChild('descriptionTextarea', { static: false }) descriptionTextarea!: ElementRef;
    @ViewChild('fileInput', { static: false }) fileInput!: ElementRef;
    
    isLogoChanged: boolean = false;
    public uploadedImages: string[] = [];
    public currentImage: string | null = null;
    isLoading = false;
    isUploadingLogo = false;
    idAgency: string | null = null;

    // Reel video properties
    selectedReelFile: File | null = null;
    selectedReelFileName: string = '';
    isUploadingReel = false;
    reelFileChanged = false;
    reelUploadedUrl: string | null = null;

    // ✅ Aperçu sous-domaine
    subdomainPreview: string = '';

    roles: Role[] = [
        { value: 'promoteur-immobilieres', viewValue: 'Promoteur Immobilier' },
        { value: 'materiaux-construction', viewValue: 'Matériaux de Construction' },
        { value: 'agence-immobilieres', viewValue: 'Agence Immobilière' },
        { value: 'agence-service', viewValue: 'Agence de Service' },
        { value: 'meilleurs-biens', viewValue: 'Meilleurs Biens' },
        { value: 'cafe-restaurant', viewValue: 'Café-Restaurant' }
    ];

    regions: { value: string; viewValue: string }[] = VILLES.map(ville => ({
        value: ville.id,
        viewValue: ville.label
    }));

    constructions: { value: string; viewValue: string }[] = MATERIAUX.map(materiel => ({
        value: materiel.value,
        viewValue: materiel.label
    }));

    CategorieServices: { value: string; viewValue: string }[] = AGENCES_SERVICES.map(service => ({
        value: service.value,
        viewValue: service.label
    }));

    sousCategorieServices: { value: string; viewValue: string }[] = [
        { value: 'leasing', viewValue: 'Leasing' },
        { value: 'SocIndustriel', viewValue: 'Sociétés Industrielles' },
        { value: 'servicesDivers', viewValue: 'Services Divers' }
    ];

    constructor(
        private agenciesService: AgenciesService,
        private location: Location,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private awsService: AwscontrolService
    ) {
        this.agencieData = new Agencies();
    }

    ngOnInit(): void {
        this.idAgency = this.activatedRoute.snapshot.paramMap.get('id');
        if (!this.idAgency) {
            Swal.fire({ icon: 'error', title: 'Erreur', text: 'Aucun ID de société fourni' });
            return;
        }
        this.loadAgencyData();
    }

    loadAgencyData(): void {
        this.isLoading = true;
        this.agenciesService.getItem(this.idAgency).subscribe(
            (data: any) => {
                this.processAgencyData(data);
                this.agencieData = { ...data };
                
                if (this.agencieData.logo_url) {
                    this.currentImage = this.cleanUrl(this.agencieData.logo_url);
                    this.uploadedImages = [this.currentImage];
                }
                
                this.subdomainPreview = this.getSubdomainPreview();

                this.reelFileChanged = false;
                this.reelUploadedUrl = null;
                this.selectedReelFile = null;
                this.selectedReelFileName = '';
                
                this.isLoading = false;
            },
            (error: any) => {
                this.isLoading = false;
                Swal.fire({ icon: "error", title: "Erreur", text: "Impossible de charger les données de la société. Veuillez réessayer." });
                console.error('Load error:', error);
            }
        );
    }

    private processAgencyData(data: any): void {
        const fieldsToClean = [
            'website_url', 'logo_url', 'facebook_url', 'instagram_url', 
            'linkedin_url', 'twitter_url', 'tiktok_url', 'videoUrl', 'videoUrl2'
        ];
        fieldsToClean.forEach(field => {
            if (data[field]) {
                data[field] = this.cleanUrl(data[field]);
            }
        });

        data.enableFacebookShare = Agencies.parseBoolean(data.enableFacebookShare);
        data.enableWhatsAppShare = Agencies.parseBoolean(data.enableWhatsAppShare);
        data.enableMessengerShare = Agencies.parseBoolean(data.enableMessengerShare);

        data.defaultShareMessage = data.defaultShareMessage || '';
        data.agencyPublicUrl = data.agencyPublicUrl || '';
        data.publicShareMessage = data.publicShareMessage || '';
        
        data.phone_number = data.phone_number || '';
        data.phone_number2 = data.phone_number2 || '';
        data.phone_number_commercial = data.phone_number_commercial || '';
        
        data.coffee = data.coffee || 'no';
    }

    private cleanUrl(url: string): string {
        if (!url || typeof url !== 'string' || url.trim() === '') {
            return '';
        }
        let cleanUrl = url.replace(/(\?|\&)v=\d+/g, '');
        cleanUrl = cleanUrl.replace(/[?&]$/, '');
        return cleanUrl;
    }

    getSubdomainPreview(): string {
        if (this.agencieData.subdomain && this.agencieData.subdomain.trim()) {
            return `https://${this.agencieData.subdomain.trim().toLowerCase()}.tunimmob.com`;
        }
        return '';
    }

    onSubdomainChange(): void {
        this.subdomainPreview = this.getSubdomainPreview();
    }

    onRoleChange(): void {
        this.agencieData.categorie = '';
        this.agencieData.sous_categorie = '';
        this.agencieData.coffee = 'no';
    }

    onCategorieChange(): void {
        if (this.agencieData.categorie !== 'servicesDivers') {
            this.agencieData.sous_categorie = '';
        }
    }

    showCategoryField(): boolean {
        return this.agencieData.role === 'materiaux-construction' || 
               this.agencieData.role === 'agence-service';
    }

    showSousCategorieField(): boolean {
        return this.agencieData.role === 'agence-service' && 
               this.agencieData.categorie === 'servicesDivers';
    }

    uploadimg2(): void {
        if (!this.selectedFiles1 || this.selectedFiles1.length === 0) return;
        if (this.selectedFiles1.length > 1) {
            Swal.fire({ icon: "warning", title: "Attention", text: "Veuillez sélectionner un seul fichier pour le logo.", timer: 3000, showConfirmButton: false });
            return;
        }
        const file = this.selectedFiles1[0];
        if (file.size > 2 * 1024 * 1024) {
            Swal.fire({ icon: "error", title: "Fichier trop volumineux", text: "La taille maximale autorisée est de 2MB.", timer: 3000, showConfirmButton: false });
            return;
        }
        if (!file.type.match(/image\/(jpeg|png|jpg|gif|webp)/)) {
            Swal.fire({ icon: "error", title: "Format non supporté", text: "Formats acceptés: JPEG, PNG, JPG, GIF, WEBP", timer: 3000, showConfirmButton: false });
            return;
        }
        this.uploadLogoToAws(file);
    }

    async uploadLogoToAws(file: File): Promise<void> {
        this.isUploadingLogo = true;
        try {
            Swal.fire({ title: 'Téléchargement en cours...', html: 'Upload du logo vers le serveur', allowOutsideClick: false, showConfirmButton: false, willOpen: () => Swal.showLoading() });
            const folder = `agency-logos/${this.idAgency}`;
            const response = await this.awsService.uploadFile(file, folder).toPromise();
            Swal.close();
            if (response.success && response.urls && response.urls.length > 0) {
                const uploadedUrl = response.urls[0];
                this.uploadedImages = [uploadedUrl];
                this.currentImage = uploadedUrl;
                this.agencieData.logo_url = uploadedUrl;
                this.isLogoChanged = true;
                Swal.fire({ icon: "success", title: "Succès", text: "Logo modifié avec succès", timer: 2000, showConfirmButton: false });
            } else {
                throw new Error(response.error || 'Upload failed');
            }
        } catch (err: any) {
            console.error('Upload error:', err);
            let errorMessage = "Impossible de modifier le logo";
            if (err.message?.includes('timeout')) errorMessage = "Délai d'attente dépassé. Veuillez réessayer.";
            else if (err.status === 413) errorMessage = "Fichier trop volumineux pour le serveur.";
            else if (err.status === 0) errorMessage = "Impossible de se connecter au serveur. Vérifiez votre connexion.";
            Swal.fire({ icon: "error", title: "Échec", text: errorMessage });
        } finally {
            this.isUploadingLogo = false;
        }
    }

    selectFileimg2(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            this.selectedFiles1 = input.files;
            this.uploadimg2();
        }
    }

    async removeImage(): Promise<void> {
        if (!this.currentImage) return;
        const result = await Swal.fire({ title: 'Supprimer le logo?', text: "Cette action ne peut pas être annulée", icon: 'warning', showCancelButton: true, confirmButtonColor: '#dc3545', cancelButtonColor: '#6c757d', confirmButtonText: 'Oui, supprimer', cancelButtonText: 'Annuler', reverseButtons: true });
        if (result.isConfirmed) {
            try {
                if (this.currentImage) await this.awsService.deleteFile(this.currentImage).toPromise();
            } catch (error) { console.warn('Could not delete logo from AWS:', error); }
            this.currentImage = null;
            this.agencieData.logo_url = '';
            this.uploadedImages = [];
            this.isLogoChanged = true;
            if (this.fileInput) this.fileInput.nativeElement.value = '';
            Swal.fire({ icon: "success", title: "Supprimé", text: "Logo supprimé avec succès", timer: 2000, showConfirmButton: false });
        }
    }

    formatText(command: string): void {
        if (this.descriptionTextarea && this.descriptionTextarea.nativeElement) {
            const textarea = this.descriptionTextarea.nativeElement;
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const selectedText = this.agencieData.description.substring(start, end);
            if (!selectedText) return;
            let formattedText = '';
            let tag = '';
            switch (command) {
                case 'bold': tag = 'strong'; break;
                case 'italic': tag = 'em'; break;
                case 'underline': tag = 'u'; break;
            }
            formattedText = `<${tag}>${selectedText}</${tag}>`;
            const newText = this.agencieData.description.substring(0, start) + formattedText + this.agencieData.description.substring(end);
            this.agencieData.description = newText;
            setTimeout(() => {
                textarea.focus();
                const newPosition = start + formattedText.length;
                textarea.setSelectionRange(newPosition, newPosition);
            }, 0);
        }
    }

    onInputChange(): void {
        if (this.agencieData.name) {
            this.agencieData.name = this.agencieData.name.replace(/[^A-Z0-9\s]/gi, '').toUpperCase();
        }
    }

    onReelFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            const file = input.files[0];
            if (!file.type.startsWith('video/')) {
                Swal.fire({ icon: 'error', title: 'Format non supporté', text: 'Veuillez sélectionner un fichier vidéo (MP4, MOV, AVI)', timer: 3000, showConfirmButton: false });
                return;
            }
            if (file.size > 100 * 1024 * 1024) {
                Swal.fire({ icon: 'error', title: 'Fichier trop volumineux', text: 'La taille maximale autorisée est de 100MB.', timer: 3000, showConfirmButton: false });
                return;
            }
            this.selectedReelFile = file;
            this.selectedReelFileName = file.name;
            this.reelFileChanged = true;
            this.uploadReelVideo();
        }
    }

    async uploadReelVideo(): Promise<void> {
        if (!this.selectedReelFile || !this.idAgency) return;
        this.isUploadingReel = true;
        try {
            Swal.fire({ title: 'Téléchargement du reel...', text: 'Veuillez patienter', allowOutsideClick: false, showConfirmButton: false, willOpen: () => Swal.showLoading() });
            const folder = 'Videos';
            const response = await this.awsService.uploadFile(this.selectedReelFile, folder).toPromise();
            Swal.close();
            if (response.success && response.urls && response.urls.length > 0) {
                this.agencieData.twitter_url = response.urls[0];
                this.reelUploadedUrl = response.urls[0];
                this.reelFileChanged = true;
                Swal.fire({ icon: 'success', title: 'Succès', text: 'Reel vidéo téléchargé avec succès', timer: 2000, showConfirmButton: false });
            } else {
                throw new Error(response.error || 'Upload failed');
            }
        } catch (err: any) {
            console.error('Reel upload error:', err);
            Swal.fire({ icon: 'error', title: 'Échec du téléchargement', text: err.message || 'Impossible de télécharger le fichier' });
            this.clearReelFile();
        } finally {
            this.isUploadingReel = false;
        }
    }

    clearReelFile(): void {
        this.selectedReelFile = null;
        this.selectedReelFileName = '';
        this.reelFileChanged = false;
        this.reelUploadedUrl = null;
        const fileInput = document.querySelector('#reelFileInput') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
    }

    clearReelUrl(): void {
        this.agencieData.twitter_url = '';
        this.reelFileChanged = true;
        this.clearReelFile();
    }

    getReelFileName(): string {
        if (this.agencieData.twitter_url) {
            const parts = this.agencieData.twitter_url.split('/');
            return parts[parts.length - 1] || 'Fichier vidéo';
        }
        return '';
    }

    updateAgencies(): void {
        if (this.isUploadingLogo || this.isUploadingReel) {
            Swal.fire({ icon: 'warning', title: 'Téléchargement en cours', text: 'Veuillez attendre la fin du téléchargement', confirmButtonColor: '#ffc107' });
            return;
        }
        this.isLoading = true;
        const updateData: any = {
            name: this.agencieData.name || null,
            email: this.agencieData.email || null,
            description: this.agencieData.description || null,
            region: this.agencieData.region || null,
            role: this.agencieData.role || null,
            adresse: this.agencieData.adresse || null,
            phone_number: this.agencieData.phone_number || null,
            phone_number2: this.agencieData.phone_number2 || null,
            phone_number_commercial: this.agencieData.phone_number_commercial || null,
            logo_url: this.agencieData.logo_url || null,
            coffee: this.agencieData.coffee || 'no',
            website_url: this.agencieData.website_url || null,
            facebook_url: this.agencieData.facebook_url || null,
            instagram_url: this.agencieData.instagram_url || null,
            linkedin_url: this.agencieData.linkedin_url || null,
            twitter_url: this.agencieData.twitter_url || null,
            tiktok_url: this.agencieData.tiktok_url || null,
            videoUrl: this.agencieData.videoUrl || null,
            videoUrl2: this.agencieData.videoUrl2 || null,
            subdomain: this.agencieData.subdomain?.trim()?.toLowerCase() || null
        };
        if (this.showCategoryField()) {
            updateData.categorie = this.agencieData.categorie || null;
            updateData.sous_categorie = this.agencieData.sous_categorie || null;
        }
        if (this.agencieData.enableFacebookShare !== undefined) updateData.enableFacebookShare = this.agencieData.enableFacebookShare;
        if (this.agencieData.enableWhatsAppShare !== undefined) updateData.enableWhatsAppShare = this.agencieData.enableWhatsAppShare;
        if (this.agencieData.enableMessengerShare !== undefined) updateData.enableMessengerShare = this.agencieData.enableMessengerShare;
        if (this.agencieData.defaultShareMessage) updateData.defaultShareMessage = this.agencieData.defaultShareMessage;
        if (this.agencieData.agencyPublicUrl) updateData.agencyPublicUrl = this.agencieData.agencyPublicUrl;
        console.log('Updating agency with data:', updateData);
        Swal.fire({ title: 'Mise à jour en cours...', text: 'Veuillez patienter', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
        this.agenciesService.updateItem(this.idAgency, updateData).subscribe({
            next: (response: any) => {
                this.isLoading = false;
                Swal.close();
                let successMessage = "Société modifiée avec succès!";
                if (typeof response === 'string') successMessage = response.trim();
                else if (response && response.message) successMessage = response.message;
                Swal.fire({ icon: "success", title: "Succès!", text: successMessage, showConfirmButton: false, timer: 2000 }).then(() => { this.location.back(); });
            },
            error: (error: any) => {
                this.isLoading = false;
                Swal.close();
                let errorMessage = "Impossible de modifier la société. Veuillez réessayer.";
                if (error.status === 200 && error.error instanceof ProgressEvent) errorMessage = "La société a été mise à jour, mais une erreur s'est produite lors de la lecture de la réponse.";
                else if (error.error && error.error.message) errorMessage = error.error.message;
                else if (error.status) errorMessage = `Erreur serveur: ${error.status}`;
                Swal.fire({ icon: "error", title: "Échec", text: errorMessage });
                console.error('Update error:', error);
            }
        });
    }

    cancel(): void {
        this.location.back();
    }

    getFileNameFromUrl(url: string): string {
        return this.awsService.extractFileName(url);
    }
}